import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Spin } from 'antd';

import ItemFilterChips from '../components/items/ItemFilterChips';
import { CHECKLIST } from '../components/icons';
import { cardWorklistFor } from '../utils/cards';
import { composeWorklistQuery } from '../utils/worklistQuery';
import { borrowedFromOf } from '../utils/linkedRecords';
import { normalizeId } from '../utils/ids';
import { RATE_LIMITED_TEXT } from '../utils/feedMessages';

/**
 * The items page's worklist mode (KPR-1566, moved out of CollectionItemsPage
 * as it stood): which card's worklist is on screen, the filter it narrows the
 * feed by, whether it can run, and the pieces the page draws for it.
 *
 * TWO hooks, because the mode sits on both sides of the feed. The feed's query
 * is composed from the latched card BEFORE the feed is fetched, while the
 * latch itself reads the card off the items the feed returns. So:
 *
 *   // before useListFilter: the latch's state, and the query the feed runs
 *   const worklistFeed = useWorklistFeed({ viewMode, cardsById, query });
 *   … useListFilter({ feedQuery: worklistFeed.feedQuery, … }) …
 *   … useListSort(…) → sortedItems …
 *   // after: the latch effect and everything derived from the card
 *   const worklist = useWorklistMode({ feed: worklistFeed, … });
 *
 * `useWorklistMode` returns { worklistFilter, worklistCardId, worklistConfig,
 * worklistCard, worklistElements, worklistUnavailable, worklistBlocked,
 * blockedBody, worklistItems, toolbarProps }; `toolbarProps` is
 * { busy, segment, filterChips }: the view switch's busy flag, its Worklist
 * segment (null where the card in view declares none), and the read-only chip
 * row of the card's filter (null while blocked).
 */
export function useWorklistFeed({ viewMode, cardsById, query }) {
	// The card whose worklist is on screen, LATCHED on entering the mode (see
	// the effect in useWorklistMode). It is held here because the feed's query
	// is composed from it, and the feed is fetched before the items the card
	// would otherwise be derived from exist.
	const [latchedWorklistCardId, setLatchedWorklistCardId] = useState(null);

	// The stored filter in force, and the query the feed actually runs. The
	// worklist's filter narrows the list SERVER-side, through the same `q` the
	// user's own filter uses — the feed is paged, so narrowing it here would
	// narrow only the page in hand.
	const worklistFilter = useMemo(() => (
		viewMode === 'worklist' && latchedWorklistCardId
			? cardWorklistFor(cardsById, latchedWorklistCardId)?.filter || ''
			: ''
	), [viewMode, latchedWorklistCardId, cardsById]);
	const { query: feedQuery } = useMemo(
		() => composeWorklistQuery({ userQuery: query, worklistFilter }),
		[query, worklistFilter]
	);

	return { latchedWorklistCardId, setLatchedWorklistCardId, worklistFilter, feedQuery };
}

export default function useWorklistMode({
	feed,
	viewMode,
	uniqueCardInFiltered,
	collectionId,
	cardFilter,
	cardsById,
	borrowedCardIds,
	getElementsForCard,
	query,
	worklistFeedError,
	firstPageFailed,
	itemsLoadError,
	itemsLoading,
	sortedItems,
	filterFields,
	retryItems,
	changeViewMode,
}) {
	const { latchedWorklistCardId, setLatchedWorklistCardId, worklistFilter } = feed;

	// Which list this is: a new collection or a change of card filter makes it
	// a different one, and the latch below must not carry across.
	const worklistListKeyRef = useRef(null);

	// Outside worklist mode the card tracks the list. INSIDE it the first
	// answer is frozen, because `uniqueCardInFiltered` reads the card off the
	// LOADED items whenever there is no explicit ?card=: applying the
	// worklist's filter can empty the list, which would unset the config,
	// which would drop the filter, which would refill the list — a request
	// loop against the API, not a flicker.
	//
	// ONE effect resets and re-latches, because two cannot. A separate "clear
	// on cardFilter change" effect queued a null that this one would only
	// answer if its own deps also changed — and pinning the card filter TO the
	// card already derived from the items leaves `uniqueCardInFiltered` on the
	// same string, so it never re-ran: the latch stayed null, the segment
	// vanished, the body fell back to the grid and the stored filter stopped
	// being sent, all with ?view=worklist still in the URL and no way back
	// short of a reload. A changed list is therefore handled HERE, as "ignore
	// prev, take what the list says now".
	useEffect(() => {
		const listKey = `${collectionId || ''}::${cardFilter || ''}`;
		const listChanged = worklistListKeyRef.current !== listKey;
		worklistListKeyRef.current = listKey;
		if (viewMode !== 'worklist' || listChanged) {
			setLatchedWorklistCardId(uniqueCardInFiltered || null);
			return;
		}
		setLatchedWorklistCardId(prev => prev || uniqueCardInFiltered || null);
		// The setter is useWorklistFeed's useState setter: stable, so naming it
		// changes nothing about when this runs.
	}, [ viewMode, uniqueCardInFiltered, collectionId, cardFilter, setLatchedWorklistCardId ]);

	// A worklist is a property of ONE card (it names that card's elements), so
	// it is offered only when the list holds exactly one — an explicit filter,
	// or a feed that happens to be single-card. `cardWorklistFor` walks the
	// parent chain, so a child card inherits its parent's list.
	const worklistCardId = viewMode === 'worklist' ? latchedWorklistCardId : uniqueCardInFiltered;
	// Never for a BORROWED card (a collection link's): working through its
	// records means writing to them, and they are read-only here.
	const worklistConfig = useMemo(
		() => (worklistCardId && !borrowedCardIds.has(normalizeId(worklistCardId)) && !borrowedFromOf(cardsById?.[ worklistCardId ])
			? cardWorklistFor(cardsById, worklistCardId)
			: null),
		[ cardsById, worklistCardId, borrowedCardIds ]
	);
	const worklistCard = worklistCardId ? cardsById?.[ worklistCardId ] : null;
	const worklistElements = worklistCardId && getElementsForCard
		? getElementsForCard(worklistCardId)
		: [];

	// Can this card's worklist run AT ALL against what the reader is looking
	// at? Composed against their current query, because that is what the feed
	// would actually send. A stored filter that will not parse (one written
	// before the card editor gated the field, or an element deleted under it)
	// or a composition past the query engine's length cap cannot narrow
	// anything — and a worklist that lists every item of the card is not a
	// lesser worklist, it is the wrong pass. So the mode is withheld and said
	// out loud rather than silently serving the unfiltered list.
	const worklistUnavailable = useMemo(() => {
		const filter = worklistConfig?.filter || '';
		if (!filter) return null;
		return composeWorklistQuery({ userQuery: query, worklistFilter: filter }).dropped;
	}, [ worklistConfig, query ]);

	// The worklist cannot be drawn: its filter will not compose, the server
	// refused the composition and the rows on screen are the previous,
	// UNFILTERED page, or the pass's first page failed to load. Any of these
	// and the pass would list rows it exists to exclude (or none it should
	// hold), so the list refuses and the Save footer never mounts.
	const worklistBlocked = viewMode === 'worklist' && worklistConfig
		? (worklistFeedError ? 'refused' : firstPageFailed ? 'failed' : worklistUnavailable)
		: null;
	const blockedBody = worklistBlocked ? (
		<div className="items-page__empty items-page__worklist-blocked">
			<Alert
				type="error"
				showIcon
				message="This worklist cannot be shown"
				description={
					worklistBlocked === 'refused' ? (
						<>
							{worklistFeedError} The items already loaded are the UNFILTERED
							list, not this card’s pass, so they are not shown here.
						</>
					) : worklistBlocked === 'failed' ? (
						itemsLoadError?.kind === 'rate_limited'
							? RATE_LIMITED_TEXT
							: 'This card’s pass could not be loaded. Try again.'
					) : worklistBlocked === 'too-long' ? (
						<>
							Together, this card’s worklist filter and your own filter are longer
							than the query engine accepts. Narrow your filter, or ask the card’s
							author to shorten theirs.
						</>
					) : (
						<>
							This card’s worklist filter could not be read, so the pass it names
							cannot be built. Ask the card’s author to fix it.
						</>
					)
				}
			/>
			<div className="items-page__worklist-blocked-actions">
				{worklistBlocked === 'failed' ? <Button onClick={retryItems}>Retry</Button> : null}
				<Button onClick={() => changeViewMode('grid')}>Show all items</Button>
			</div>
		</div>
	) : null;

	const worklistItems = useMemo(() => sortedItems.filter(item => !item?.linkedFrom), [sortedItems]);

	// The card's narrowing, shown next to the reader's own. Read-only, because
	// it is the card author's filter and not theirs to edit here — but VISIBLE,
	// because a list narrowed by something with no representation on screen is
	// a list that lies about what it holds. Withheld while the worklist is
	// blocked; there the Alert is the whole story.
	const filterChips = worklistFilter && !worklistBlocked ? (
		<div className="items-page__worklist-filter">
			<span className="items-page__worklist-filter-label">Worklist filter</span>
			<ItemFilterChips
				query={worklistFilter}
				fields={filterFields}
				readOnly
				className="items-page__filter-chips"
			/>
		</div>
	) : null;

	// A worklist with a stored filter asks the server a different question, so
	// the toggle now costs a round trip. Say so on the segment the tap landed
	// on, rather than letting the unfiltered list flash past on the way.
	const worklistSwitching = Boolean(worklistFilter) && itemsLoading;
	const worklistIcon = worklistSwitching
		? <Spin size="small" aria-label="Loading the worklist" />
		: <CHECKLIST role="img" aria-label="Worklist" />;

	// A worklist whose filter cannot run would list every item of the card as
	// the pass. The segment stays visible — the reader should see that this
	// card HAS a worklist — but it does not open, and it says why on hover.
	const worklistDisabled = Boolean(worklistUnavailable);
	const worklistTitle = worklistUnavailable === 'too-long'
		? 'Worklist unavailable: this card’s filter and yours are too long together'
		: worklistUnavailable
			? 'Worklist unavailable: this card’s filter could not be read'
			: 'Worklist';

	// The view switch's third segment appears only where it means something: a
	// single card that declares what "working through" its items is.
	const segment = worklistConfig ? {
		value: 'worklist',
		label: 'Worklist',
		title: worklistTitle,
		disabled: worklistDisabled,
		icon: worklistSwitching ? worklistIcon : undefined,
		mobileIcon: worklistIcon,
	} : null;

	return {
		worklistFilter,
		worklistCardId,
		worklistConfig,
		worklistCard,
		worklistElements,
		worklistUnavailable,
		worklistBlocked,
		blockedBody,
		worklistItems,
		toolbarProps: { busy: worklistSwitching, segment, filterChips },
	};
}
